/**
 * Roadmap Kanban — Phase 1.5 of the Feature Backlog initiative.
 *
 * Six columns matching the lifecycle:
 *   INTAKE → TODO → PLANNED → IN-PROGRESS → QA → SHIPPED
 *
 * Excluded from Kanban (still accessible via List view filter):
 *   declined, duplicate (terminal), soft-deleted items, and the deprecated
 *   'todo' enum value (kept in DB for legacy rows; no UI surface).
 *
 * Drag a card across columns → fires adminRoadmapService.patch with the new
 * status. Optimistic UI move; on server error we restore the original state
 * and surface the failure via window.alert (matches the admin page's
 * existing error pattern in handleDelete / handleTogglePin).
 *
 * Card click → opens the existing RoadmapEditorModal via the parent's
 * onEdit callback.
 *
 * This is admin-only (web), so we follow the admin-portal convention of
 * raw HTML + inline styles via T design tokens (same as AdminRoadmap.tsx
 * and RoadmapEditorModal.tsx).
 */
import React, { useCallback, useMemo, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
// Migration 1731 — within-column reorder rides the same DndContext as the
// existing cross-column status drag (multi-container sortable pattern).
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronUp, MessageSquare, ExternalLink } from 'lucide-react';
import { T, card, badge } from '../../utils/adminDesignTokens';
import {
  adminRoadmapService,
  type AdminRoadmapItem,
  type RoadmapStatus,
  STATUS_LABEL,
  STATUS_COLORS,
  CATEGORY_LABEL,
  CATEGORY_COLORS,
  SOURCE_LABEL,
  // Phase 4: severity palette for card stripe
  SEVERITY_COLORS,
  FEATURE_STRIPE_COLOR,
} from '../../services/adminRoadmapService';

// ── Column ordering ─────────────────────────────────────────────────
//
// Order matches the lifecycle direction (left→right is "further along").
// Statuses NOT in this list are excluded from Kanban entirely; admins
// access those (declined, duplicate) via the List view's status filter
// dropdown.
//
// Phase 1.6 flow refinement: under_review is back in (active state, "team
// is looking at this", user-visible if visibility=public). 'todo' was
// dropped from UI dropdowns and Kanban — the enum value persists in the
// DB for legacy rows but never appears as a column.
//
// Phase 6 polish v16 (2026-05-12): 'stage' added as a column between
// 'in_progress' and 'qa'. Workflow is: build (in_progress) → deploy
// to staging (stage) → validate (qa) → ship to prod (shipped). Adding
// 'stage' to the inline list dropdown + VALID_STATUSES + enum without
// adding it here meant stage-rows DISAPPEARED from Kanban — the
// forensic pass caught this before users hit it.

const KANBAN_STATUSES: RoadmapStatus[] = [
  'intake',
  'under_review',
  'planned',
  'in_progress',
  'stage',
  'qa',
  'shipped',
];

interface RoadmapKanbanProps {
  items: AdminRoadmapItem[];
  /** Called after a successful drag-drop status change. Parent should
   *  reload the list to refresh derived data (composition strip etc.). */
  onItemUpdated: (updated: AdminRoadmapItem) => void;
  /** Card click → opens the editor modal. Parent owns the modal state. */
  onEdit: (item: AdminRoadmapItem) => void;
}

export const RoadmapKanban: React.FC<RoadmapKanbanProps> = ({
  items,
  onItemUpdated,
  onEdit,
}) => {
  // Local mirror of items so optimistic drops can update state instantly
  // without round-tripping through the parent. Parent's items array is
  // refreshed via onItemUpdated after server confirmation.
  const [localItems, setLocalItems] = useState<AdminRoadmapItem[]>(items);
  const [draggingId, setDraggingId] = useState<string | null>(null);

  // Re-sync when parent reloads
  React.useEffect(() => {
    setLocalItems(items);
  }, [items]);

  // PointerSensor with a small distance threshold prevents drags from
  // hijacking simple click-to-edit interactions.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  // Group items by status. Items with statuses outside KANBAN_STATUSES
  // (declined, duplicate, legacy 'todo' rows) don't appear at all —
  // admins find them in List view via the status filter dropdown.
  //
  // Phase 5: also filter out items archived from Kanban
  // (archived_from_kanban_at !== null). Once the daily 7am ET cron
  // archives a shipped item, it disappears from the Kanban shipped
  // lane and surfaces only on /admin/roadmap?view=completed.
  const itemsByStatus = useMemo(() => {
    const grouped: Record<string, AdminRoadmapItem[]> = {};
    for (const status of KANBAN_STATUSES) grouped[status] = [];
    for (const item of localItems) {
      // Phase 5 filter: exclude archived items
      if (item.archived_from_kanban_at) continue;
      if (KANBAN_STATUSES.includes(item.status)) {
        grouped[item.status].push(item);
      }
    }
    // Migration 1731 — within each column, display order follows
    // kanban_manual_rank; un-dragged cards keep their prior order (Infinity
    // tail + stable sort).
    for (const status of KANBAN_STATUSES) {
      grouped[status].sort(
        (a, b) => (a.kanban_manual_rank ?? Infinity) - (b.kanban_manual_rank ?? Infinity),
      );
    }
    return grouped;
  }, [localItems]);

  const draggingItem = useMemo(
    () => localItems.find((i) => i.id === draggingId) ?? null,
    [localItems, draggingId],
  );

  const handleDragStart = useCallback((e: DragStartEvent) => {
    setDraggingId(String(e.active.id));
  }, []);

  const handleDragEnd = useCallback(
    async (e: DragEndEvent) => {
      setDraggingId(null);
      const activeId = String(e.active.id);
      if (!e.over) return;
      const overId = String(e.over.id);

      const item = localItems.find((i) => i.id === activeId);
      if (!item) return;
      const activeStatus = item.status;

      // `over` is either a COLUMN id (a KANBAN_STATUS) or a CARD id. Resolve it
      // to a status so we can distinguish a cross-column MOVE from a
      // within-column REORDER (migration 1731).
      const overStatus = KANBAN_STATUSES.includes(overId as RoadmapStatus)
        ? (overId as RoadmapStatus)
        : localItems.find((i) => i.id === overId)?.status ?? null;
      if (!overStatus || !KANBAN_STATUSES.includes(overStatus)) return;

      // ── Cross-column: change status (existing behavior) ──
      if (activeStatus !== overStatus) {
        const previousStatus = activeStatus;
        setLocalItems((prev) =>
          prev.map((i) => (i.id === activeId ? { ...i, status: overStatus } : i)),
        );
        try {
          const updated = await adminRoadmapService.patch(item.id, {
            status: overStatus,
          });
          // Parent reloads to refresh the composition strip, source filter
          // counts, etc. The local state already reflects the move.
          onItemUpdated(updated);
        } catch (err: any) {
          // Roll back optimistic move and surface the error
          setLocalItems((prev) =>
            prev.map((i) => (i.id === activeId ? { ...i, status: previousStatus } : i)),
          );
          // eslint-disable-next-line no-alert
          window.alert(
            `Failed to move "${item.title}" to ${STATUS_LABEL[overStatus]}: ${
              err?.message ?? 'unknown error'
            }`,
          );
        }
        return;
      }

      // ── Within-column: reorder (migration 1731) ──
      if (overId === activeId) return; // dropped on itself — no-op
      const colIds = itemsByStatus[activeStatus].map((i) => i.id);
      const oldIndex = colIds.indexOf(activeId);
      const newIndex = colIds.indexOf(overId);
      if (oldIndex === -1 || newIndex === -1) return;
      const newIds = arrayMove(colIds, oldIndex, newIndex);

      // Optimistic: write evenly-spaced ranks onto this column's cards.
      const snapshot = localItems;
      const rankById = new Map(newIds.map((id, idx) => [id, idx]));
      setLocalItems((prev) =>
        prev.map((i) =>
          rankById.has(i.id) ? { ...i, kanban_manual_rank: rankById.get(i.id)! } : i,
        ),
      );
      try {
        await adminRoadmapService.reorder('kanban', newIds);
      } catch (err: any) {
        setLocalItems(snapshot); // revert
        // eslint-disable-next-line no-alert
        window.alert(`Reorder failed: ${err?.message ?? 'unknown error'}`);
      }
    },
    [localItems, itemsByStatus, onItemUpdated],
  );

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div
        style={{
          display: 'flex',
          gap: 12,
          overflowX: 'auto',
          paddingBottom: 12,
          // Width threshold from 03-deferred-to-phase-1-5.md: six fixed
          // ~280px columns + gaps overflow horizontally below ~1700px.
        }}
      >
        {KANBAN_STATUSES.map((status) => (
          <KanbanColumn
            key={status}
            status={status}
            items={itemsByStatus[status]}
            isDraggingActive={draggingId !== null}
            onEdit={onEdit}
          />
        ))}
      </div>

      {/* DragOverlay renders the floating card under the cursor. Lifting
          this out of the source column avoids re-layout flicker as the
          source column shrinks. */}
      <DragOverlay>
        {draggingItem ? <Card item={draggingItem} dragging /> : null}
      </DragOverlay>
    </DndContext>
  );
};

// ── Column ──────────────────────────────────────────────────────────

interface KanbanColumnProps {
  status: RoadmapStatus;
  items: AdminRoadmapItem[];
  isDraggingActive: boolean;
  onEdit: (item: AdminRoadmapItem) => void;
}

const KanbanColumn: React.FC<KanbanColumnProps> = ({
  status,
  items,
  isDraggingActive,
  onEdit,
}) => {
  const { setNodeRef, isOver } = useDroppable({ id: status });
  const colors = STATUS_COLORS[status];
  const count = items.length;

  // Per 03-deferred-to-phase-1-5.md: when a column has zero cards,
  // render a faint dashed-border drop zone with the column name so
  // admins can drop INTO an empty column.
  const isEmpty = count === 0;

  return (
    <div
      ref={setNodeRef}
      style={{
        flex: '0 0 280px',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--surface)',
        borderRadius: T.radiusMd,
        border: isOver
          ? `2px solid ${colors.fg}`
          : isEmpty
            ? `2px dashed ${T.limestone}`
            : `1px solid ${T.limestone}`,
        // IMPORTANT: do NOT apply CSS `transform` on this wrapper.
        // A non-`none` transform creates a containing block for the
        // DragOverlay portal (which uses `position: fixed` against the
        // viewport), causing the floating card to render hundreds of
        // pixels offset from the cursor. Phase 1.7 fix.
        transition: 'border 80ms ease',
        minHeight: 220,
      }}
    >
      <div
        style={{
          padding: '10px 12px',
          borderBottom: isEmpty ? 'none' : `1px solid ${T.limestone}`,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span
          style={{
            ...badge(colors.bg, colors.fg),
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          }}
        >
          {STATUS_LABEL[status]}
        </span>
        <span
          style={{
            fontSize: 12,
            color: T.textTertiary,
            fontVariantNumeric: 'tabular-nums',
            marginLeft: 'auto',
          }}
        >
          {count}
        </span>
      </div>

      {isEmpty ? (
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px 12px',
            textAlign: 'center',
            fontSize: 12,
            color: T.textTertiary,
            lineHeight: 1.4,
          }}
        >
          {isDraggingActive
            ? `Drop here to move to ${STATUS_LABEL[status]}`
            : `No items in ${STATUS_LABEL[status]}`}
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            padding: 8,
            overflowY: 'auto',
            // Cap visible cards per column so a runaway INTAKE doesn't
            // dominate the entire viewport. Admins scroll within the
            // column to see more.
            maxHeight: 'calc(100vh - 320px)',
          }}
        >
          <SortableContext
            items={items.map((i) => i.id)}
            strategy={verticalListSortingStrategy}
          >
            {items.map((item) => (
              <DraggableCard key={item.id} item={item} onEdit={onEdit} />
            ))}
          </SortableContext>
        </div>
      )}
    </div>
  );
};

// ── Draggable card wrapper ──────────────────────────────────────────

interface DraggableCardProps {
  item: AdminRoadmapItem;
  onEdit: (item: AdminRoadmapItem) => void;
}

const DraggableCard: React.FC<DraggableCardProps> = ({ item, onEdit }) => {
  // Migration 1731 — useSortable (not useDraggable) so cards are reorder
  // targets within their column. Cross-column status drag still works because
  // the column stays a useDroppable and handleDragEnd resolves either target.
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.id });

  // While being dragged, hide the source card (DragOverlay renders the
  // floating one). Otherwise card content jumps as the column reflows.
  // Non-dragging siblings animate via transform to open the drop gap.
  const style: React.CSSProperties = isDragging
    ? { opacity: 0 }
    : { cursor: 'grab', transform: CSS.Transform.toString(transform), transition };

  // Click handler is on the card body, not the drag handle area, so
  // a click without movement opens the editor modal. PointerSensor's
  // 6px activation distance prevents click-vs-drag conflicts.
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={() => {
        if (!isDragging) onEdit(item);
      }}
    >
      <Card item={item} />
    </div>
  );
};

// ── Card (presentational, used both in column and in DragOverlay) ────

interface CardProps {
  item: AdminRoadmapItem;
  dragging?: boolean;
}

const Card: React.FC<CardProps> = ({ item, dragging = false }) => {
  const categoryColors = CATEGORY_COLORS[item.category];
  const statusColors = STATUS_COLORS[item.status];
  const sourceLabel =
    item.source && item.source !== 'admin_manual'
      ? SOURCE_LABEL[item.source]
      : null;

  return (
    <div
      style={{
        ...card,
        padding: 10,
        // Phase 4: 4px colored left-border stripe encodes the item's
        // classification — red/amber/green for known_issue severities,
        // blue for features. Color palette lives in adminRoadmapService.ts
        // so the same colors render on the List view rows + any future
        // surfaces.
        // Phase 6 polish v30 (2026-05-15): needs_triage grey stripe
        // removed alongside the "?" marker. Unclassified items just
        // render the feature-blue stripe (the absence of severity is
        // its own signal).
        borderLeft: `4px solid ${
          item.severity
            ? SEVERITY_COLORS[item.severity].stripe
            : FEATURE_STRIPE_COLOR
        }`,
        // Tighten left padding so the stripe doesn't push content right
        paddingLeft: 8,
        boxShadow: dragging
          ? '0 8px 24px rgba(0,0,0,0.18)'
          : T.shadowMd,
        // Slight tilt on the floating overlay card to reinforce drag affordance
        transform: dragging ? 'rotate(1.5deg)' : 'none',
        cursor: dragging ? 'grabbing' : 'pointer',
        userSelect: 'none',
      }}
    >
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: T.textPrimary,
          marginBottom: 6,
          // Truncate to ~2 lines per 03-deferred-to-phase-1-5.md card spec
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          lineHeight: 1.3,
        }}
      >
        {item.title}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          flexWrap: 'wrap',
        }}
      >
        {/* Phase 6 polish v30 (2026-05-15): needs_triage "?" marker
            removed. Admins no longer need a visible "not-yet-classified"
            pip — empty category/severity fields already convey the same
            information without adding chrome. */}
        {/* Status pill — visible per-card so admins don't have to
            depend on the column header (which scrolls out of view in
            tall columns). Uses STATUS_COLORS so it matches the column
            header palette. Phase 1.7 addition. */}
        <span
          style={{
            ...badge(statusColors.bg, statusColors.fg),
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.03em',
            textTransform: 'uppercase',
          }}
        >
          {STATUS_LABEL[item.status]}
        </span>
        <span
          style={{
            ...badge(categoryColors.bg, categoryColors.fg),
            fontSize: 10,
          }}
        >
          {CATEGORY_LABEL[item.category]}
        </span>
        {sourceLabel && (
          <span
            style={{
              ...badge(T.limestone, T.textSecondary),
              fontSize: 10,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
            }}
          >
            <ExternalLink size={9} />
            {sourceLabel}
          </span>
        )}
        {item.vote_count > 0 && (
          <span
            style={{
              fontSize: 11,
              color: T.textSecondary,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 2,
              marginLeft: 'auto',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            <ChevronUp size={11} />
            {item.vote_count}
          </span>
        )}
        {item.comment_count > 0 && (
          <span
            style={{
              fontSize: 11,
              color: T.textSecondary,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 2,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            <MessageSquare size={10} />
            {item.comment_count}
          </span>
        )}
      </div>
    </div>
  );
};
